import { useMemo } from "react";
import Button from "../../../../../shared/components/Button";
import Icons from "../../../../../shared/constants/icon.constant";
import SecondaryIcon from "../../../../../shared/components/SecondaryIcon";
import AvailableBalance from "./AvailableBalance";
import { useFiatBalances } from "@/features/admin/services/fiat";

interface BankingFundsOverviewProps {
  onWithdraw(): void;
  onAddFunds(): void;
}

export default function BankingFundsOverview({
  onWithdraw,
  onAddFunds,
}: Readonly<BankingFundsOverviewProps>) {
  const { data: balances = [], isLoading, isError } = useFiatBalances();

  const balanceData = useMemo(() => {
    const baseCurrency = balances[0]?.name?.toUpperCase() ?? "USD";

    if (isError) {
      return {
        amountDisplay: "0.00",
        currencyLabel: baseCurrency,
        errorMessage: "Failed to load balance",
      };
    }

    const totalInUSD = balances.reduce((total, balance) => {
      const amount = parseFloat(balance.amount) || 0;
      const rate = balance.rate || 1;
      return total + amount * rate;
    }, 0);

    return {
      amountDisplay: totalInUSD.toFixed(2),
      currencyLabel: baseCurrency,
      errorMessage: undefined,
    };
  }, [balances, isError]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center my-6">
        <AvailableBalance
          amountDisplay={balanceData.amountDisplay}
          currencyLabel={balanceData.currencyLabel}
          errorMessage={balanceData.errorMessage}
          isLoading={isLoading}
        />
      </div>
      <div className="flex flex-row my-2 gap-4">
        <Button
          variant="secondary"
          className="bg-main-dark/50! rounded-lg! gap-2 justify-between! py-3 text-label-dark!"
          fluid
          onClick={onAddFunds}>
          <SecondaryIcon icon={Icons.Deposit({ width: 16, height: 16 })} />
          Top Up
        </Button>
        <Button
          variant="secondary"
          className="bg-main-dark/50! rounded-lg! gap-2 justify-between! py-3 text-label-dark!"
          fluid
          onClick={onWithdraw}>
          <SecondaryIcon icon={Icons.Withdrawal({ width: 16, height: 16 })} />
          Withdraw
        </Button>
      </div>
    </div>
  );
}
